import { Client, Context, CustomAuthenticationProvider, Middleware, MiddlewareFactory } from "@microsoft/microsoft-graph-client/lib/src/browser";

// Secondary, best-effort signal ONLY. These message strings are implementation-defined (and may be
// localized), so they can never be authoritatively verified against a spec and must never be the sole
// basis for a decision - they only widen coverage for non-conformant runtimes, never narrow it.
//   Chrome/Edge (incl. new Outlook WebView2): "Failed to fetch"
//   Firefox: "NetworkError when attempting to fetch resource"
//   Safari/WebKit: "Load failed"
const TRANSIENT_FETCH_ERROR = /failed to fetch|networkerror|network error|load failed/i;

/**
 * True only for transport-level failures where no HTTP response was received.
 *
 * The authoritative signal is the error *type*, not its message: the WHATWG Fetch Standard requires
 * fetch() to reject a network error (DNS failure, connection reset, a request dropped in flight) with
 * a TypeError. This is verifiable and stable, unlike the message text. We check `name === "TypeError"`
 * rather than `instanceof TypeError` so it survives realm boundaries (WebView/iframe), where
 * `instanceof` can silently fail. Graph's own errors are excluded: GraphError carries a numeric
 * statusCode (a real HTTP response), and both GraphError and GraphClientError report name "Error".
 */
const isTransientNetworkError = (error: any): boolean => {
    if (!error) {
        return false;
    }
    // A numeric statusCode means a real HTTP response came back - never a transport failure.
    if (typeof error.statusCode === "number" && error.statusCode > 0) {
        return false;
    }
    if (error.name === "TypeError") {
        return true;
    }
    // Fallback for runtimes that don't surface a network error as a TypeError (non-authoritative).
    return TRANSIENT_FETCH_ERROR.test((error.message ?? "").toString());
};

const IDEMPOTENT_METHODS = new Set(["GET", "HEAD", "DELETE"]);
// POST endpoints Graph models as POST but that mutate nothing - a read expressed as a POST (search).
const READONLY_POST_PATHS = /\/search\/query(?:$|\?)/i;
const BATCH_PATH = /\/\$batch(?:$|\?)/i;

/**
 * A JSON `$batch` is replayable only when every sub-request is itself idempotent. A batch is not
 * atomic, but if no sub-request has a side-effect then re-running the whole batch is side-effect
 * free. An unparseable/empty payload, or any non-idempotent (or method-less) sub-request, is unsafe.
 * Inspecting the actual methods keeps this correct as the code evolves: add a write to a batch later
 * and it stops being retried automatically.
 */
const isAllIdempotentBatch = (body: unknown): boolean => {
    if (typeof body !== "string") {
        return false;
    }
    try {
        const requests = JSON.parse(body)?.requests;
        return Array.isArray(requests) && requests.length > 0
            && requests.every((r: any) => IDEMPOTENT_METHODS.has(String(r?.method ?? "").toUpperCase()));
    } catch {
        return false; // A payload we cannot read is a payload we must not replay.
    }
};

/**
 * A transport failure carries no proof of whether the server processed the request, so a replay is
 * safe only when re-executing the operation cannot produce a duplicate side-effect. Two things must
 * both hold:
 *
 *   1. Idempotency. GET/HEAD/DELETE are inherently idempotent (RFC 7231) and always safe to repeat.
 *      POST/PATCH are not: a dropped response after a successful `POST .../send` would double-send,
 *      or replay against the now-moved draft and surface a 404 that makes a real send look failed.
 *      The exceptions are POSTs that provably have no side-effect: read-only paths Graph models as
 *      POST (search), and a `$batch` whose every sub-request is itself idempotent (see above).
 *   2. A replayable body. Even a repeatable request needs a re-sendable body: a string (JSON) or
 *      none replays cleanly; a streamed upload (Blob/ArrayBuffer/FormData/ReadableStream) would be
 *      consumed/corrupted, so it is never replayed.
 */
const isSafeToReplay = (context: Context): boolean => {
    const request = context.request;
    const isStringRequest = typeof request === "string";
    const method: string = ((isStringRequest ? context.options?.method : request?.method) ?? "GET").toUpperCase();

    if (IDEMPOTENT_METHODS.has(method)) {
        return true;
    }
    if (method !== "POST") {
        return false; // PATCH/PUT and the rest are never auto-replayed.
    }

    const url = isStringRequest ? request : (request?.url ?? "");
    const body = context.options?.body;

    // $batch: replay only when it wraps nothing but idempotent sub-requests.
    if (BATCH_PATH.test(url)) {
        return isAllIdempotentBatch(body);
    }
    // Read-only POST (search): replay when the body itself is re-sendable (string/JSON or none).
    if (READONLY_POST_PATHS.test(url)) {
        return body === undefined || body === null || typeof body === "string";
    }
    return false;
};

/**
 * Retries transient transport failures (see {@link isTransientNetworkError}) that the Graph SDK's
 * built-in RetryHandler never sees, because a rejected fetch() throws rather than returning a
 * response. These failures arrive in bursts when the Outlook task pane is refreshed rapidly and the
 * host drops in-flight requests even though the JS context survives; without a retry the operation
 * fails outright, degrading the UI to empty results and flooding the logs. Retrying recovers the
 * blip; a genuine outage still surfaces once the small retry budget is exhausted. Only the failure
 * path is delayed - successful requests are untouched.
 */
export class NetworkRetryHandler implements Middleware {
    private nextMiddleware: Middleware;

    constructor(
        private readonly maxRetries: number = 2,
        private readonly baseDelayMs: number = 400,
    ) { }

    public async execute(context: Context): Promise<void> {
        let attempt = 0;
        for (; ;) {
            try {
                await this.nextMiddleware.execute(context);
                return;
            } catch (error) {
                if (attempt < this.maxRetries && isTransientNetworkError(error) && isSafeToReplay(context)) {
                    attempt++;
                    await new Promise<void>(resolve => setTimeout(resolve, this.baseDelayMs * attempt));
                    continue;
                }
                throw error;
            }
        }
    }

    public setNext(next: Middleware): void {
        this.nextMiddleware = next;
    }
}

export const getAuthenticatedClient = (accessToken: string): Client => {
    // Preserve the exact auth wrapping Client.init used before (CustomAuthenticationProvider around a
    // static-token callback), then prepend the network-retry handler to the default middleware chain
    // (auth -> retry(429/503) -> telemetry -> http). Behaviour is otherwise unchanged.
    const authProvider = new CustomAuthenticationProvider((done) => done(null, accessToken));
    const middleware = MiddlewareFactory.getDefaultMiddlewareChain(authProvider);
    middleware.unshift(new NetworkRetryHandler());
    return Client.initWithMiddleware({ middleware });
};
